// Ported from dev-scrips-pm-react billing print-preview screen (PROD-1001 / PROD-1119 cross-module closure).
/**
 * PrintPreviewDialog — the single mounted reader for `usePrintPreviewStore`.
 *
 * Flutter's invoice/receipt Print does NOT open a browser tab — it fetches the
 * PDF and shows it in an in-app preview screen (`PrintInvoiceState` /
 * `PrintReceiptState` → PDF viewer; `PdfUtils.printPdf`). The print mutations
 * (`usePrintInvoice` / `usePrintReceipt`, `use-invoice-actions.ts`) already
 * resolve the PDF and push it into the Zustand store via `.open(url, …)`, but
 * until now nothing rendered it — the URL was resolved and discarded. This
 * component closes that loop: mounted once at the app shell, it subscribes to
 * the store and shows the resolved PDF in an `<iframe>`.
 *
 * PRINT RELIABILITY (the crux). A browser can only call `iframe.contentWindow
 * .print()` when the iframe document is SAME-ORIGIN; a cross-origin Azure Blob
 * SAS link throws `SecurityError`, so the previous "best-effort" print silently
 * no-op'd for the SAS branch (the common case). We fix that here:
 *
 *   • `blob:` / `data:` URLs (the base64 branch of `resolvePdfResponseToUrl`)
 *     are already same-origin → `contentWindow.print()` works directly.
 *   • A cross-origin SAS URL is fetched into a same-origin `blob:` URL, which we
 *     then show in the iframe — now `contentWindow.print()` (and `autoPrint`)
 *     are reliable. The SAS link carries its own auth in the query string, so
 *     the fetch is credential-less.
 *   • If that fetch is CORS-blocked (a SAS container without CORS rules), we
 *     keep the SAS link in the iframe for VIEWING and route Print through a
 *     top-level `window.open(...)` tab — a top-level navigation is not subject
 *     to the cross-origin iframe SecurityError, so printing stays reliable. We
 *     never depend on `contentWindow.print()` against a cross-origin frame.
 *
 * `autoPrint` (true for the "Print" affordance, false for "View Receipt")
 * mirrors Flutter's `printPdf` auto-invoke: it fires only once a same-origin
 * document has loaded. For the CORS-blocked fallback it stays a no-op (a
 * non-user-gesture `window.open` would be popup-blocked); the visible Print
 * button remains the reliable path.
 *
 * FRAMING (second Codex pass, PROD-1001 P1). Validation alone doesn't help if
 * the un-validated response still gets framed while we wait — or after we give
 * up. So a raw (non blob:/data:) url is NEVER assigned as the iframe `src`.
 * Only two things may ever appear in the iframe: a `blob:`/`data:` url handed
 * in directly (already same-origin), or the same-origin blob THIS component
 * mints after fetching + magic-byte-validating the SAS bytes. While that fetch
 * is in flight, or once it fails (CORS/network/non-PDF), we render a DS error
 * state instead of the frame — never the raw url, same-origin or not.
 *
 * State is DATA off the store (url / title / autoPrint) — this component bakes
 * in no invoice/receipt knowledge and no role logic; any caller that resolves a
 * PDF drives it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
// DS mapping: dialog → Modal (controlled open/onClose + title/footer/children),
// button → Button, print/new-tab glyphs → DS Icon ('print' / 'external'),
// non-framable fallback → Banner (error).
import { Modal, Button, Banner, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { usePrintPreviewStore } from '@/billing/store/print-preview-store';
import { ExternalLink, Printer } from 'lucide-react';

/**
 * Load outcome for a cross-origin SAS url being upgraded into a same-origin
 * blob, tagged with BOTH the url it applies to AND the store's open-
 * generation at the time this outcome was produced. The generation tag is
 * what makes a reopen of the IDENTICAL SAS url a fresh load rather than a hit
 * against a stale (possibly already-revoked) cache entry — see `generation`
 * on `usePrintPreviewStore` (Codex v6 P2).
 */
type SasLoadState =
  | { forUrl: string; forGeneration: number; status: 'ready'; blobUrl: string }
  | { forUrl: string; forGeneration: number; status: 'error' };

export function PrintPreviewDialog() {
  const url = usePrintPreviewStore((s) => s.url);
  const title = usePrintPreviewStore((s) => s.title);
  const autoPrint = usePrintPreviewStore((s) => s.autoPrint);
  const generation = usePrintPreviewStore((s) => s.generation);
  const close = usePrintPreviewStore((s) => s.close);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // One-shot guard: autoPrint fires once per printable document, not on every
  // re-render / re-load (React StrictMode double-mounts in dev, and the browser
  // PDF viewer can emit `load` more than once).
  const autoPrinted = useRef(false);
  // The pending ~60ms deferred-print timer (see `handleIframeLoad`). Tracked so
  // it can be cancelled on url change / close / unmount — otherwise it retains
  // the OLD iframe's `contentWindow` and can fire `.print()` after the dialog
  // closed, the url changed to a different document, or the user logged out;
  // by then the iframe may be gone or showing a different (possibly revoked)
  // document (Codex v6 P2).
  const autoPrintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearAutoPrintTimer = useCallback(() => {
    if (autoPrintTimer.current != null) {
      clearTimeout(autoPrintTimer.current);
      autoPrintTimer.current = null;
    }
  }, []);

  // Outcome of the SAS→same-origin-blob upgrade ('ready'/'error'), tagged with
  // the url AND open-generation it applies to so a stale result is ignored
  // once the store url changes OR the SAME url is reopened (a fresh
  // `open()` bumps `generation` even when the url string is unchanged —
  // Codex v6 P2). There is no 'pending' member and no synchronous setState in
  // the effect below — `null` (initial) and "sasLoad doesn't match (url,
  // generation)" (stale, from a prior load) both fall through to the same
  // render-derived loading state (the `else url` branch further down), so
  // pending is DERIVED, not stored.
  const [sasLoad, setSasLoad] = useState<SasLoadState | null>(null);

  const open = url != null;

  // Pure render-time derivations (no effect / no sync setState):
  //  • blob:/data: urls are already same-origin → printable inline directly.
  //  • a cross-origin SAS url becomes printable inline only after the async
  //    upgrade below mints a same-origin blob for THIS (url, generation).
  const isDirectSameOrigin =
    url != null && (url.startsWith('blob:') || url.startsWith('data:'));
  const currentSasLoad =
    url != null && sasLoad?.forUrl === url && sasLoad.forGeneration === generation
      ? sasLoad
      : null;
  const upgradedUrl = currentSasLoad?.status === 'ready' ? currentSasLoad.blobUrl : null;
  const sasLoadFailed = !isDirectSameOrigin && currentSasLoad?.status === 'error';
  // The iframe `src`. This is the ONLY value ever handed to the iframe — a raw
  // (non blob:/data:) url is NEVER assigned here, whether we're still waiting
  // on the upgrade fetch or it has already failed. That keeps an un-sandboxed,
  // un-validated response from ever executing as active content (PROD-1001
  // second review, P1): the non-PDF/error/pending case renders the DS error
  // state below instead of a frame.
  const iframeUrl = url == null ? null : isDirectSameOrigin ? url : upgradedUrl;
  const printableInline = iframeUrl != null;

  // Side effect ONLY: for a cross-origin SAS url, pull the bytes into a
  // same-origin blob so Print + autoPrint become reliable, and so the iframe
  // has something safe to show at all. A CORS-blocked / non-PDF fetch records
  // an error outcome — Print routes through open-in-new-tab and the dialog
  // shows a refuse-to-frame error state instead of ever touching the raw url.
  //
  // Keyed on `generation` (in addition to `url`) so a REOPEN of the identical
  // SAS url — the backend can return the same SAS link for a later print —
  // always re-runs this effect and starts a fresh fetch, rather than the
  // dialog trusting whatever `sasLoad` happened to be left over from the
  // prior time this url was open (which may have since been revoked on
  // close). This component is mounted once for the app's lifetime, so that
  // leftover state does NOT get cleared by an unmount between opens
  // (Codex v6 P2).
  useEffect(() => {
    autoPrinted.current = false; // ref mutation, not setState — re-arm per url
    if (url == null || isDirectSameOrigin) return;
    const myGeneration = generation;

    let cancelled = false;
    // Stop the network download itself on cleanup (unmount / url change) — the
    // `cancelled` flag alone only suppressed the state update after the bytes
    // had already finished downloading; large PHI documents kept streaming in
    // the background after the preview closed. (PROD-1001 second review, P2.)
    const controller = new AbortController();
    let mintedBlobUrl: string | null = null;
    void (async () => {
      try {
        // SAS links self-authenticate via the query string; omit cookies.
        const res = await fetch(url, { credentials: 'omit', signal: controller.signal });
        if (!res.ok) throw new Error(`print fetch failed: ${res.status}`);
        // SECURITY: never promote un-validated remote bytes to our origin. A SAS
        // container that returns HTML/SVG would otherwise be minted into a
        // same-origin object URL and could execute active content (localStorage
        // tokens + PHI) inside the iframe. We read the raw bytes, require the
        // `%PDF` magic header, and re-wrap them with an explicit application/pdf
        // type so the minted url can never carry an active MIME. (PROD-1001
        // review, P1.)
        const buffer = await res.arrayBuffer();
        if (cancelled) return;
        const bytes = new Uint8Array(buffer);
        const isPdf =
          bytes.length >= 5 &&
          bytes[0] === 0x25 && // %
          bytes[1] === 0x50 && // P
          bytes[2] === 0x44 && // D
          bytes[3] === 0x46 && // F
          bytes[4] === 0x2d; //  -
        if (!isPdf) throw new Error('print fetch: response is not a PDF');
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        mintedBlobUrl = URL.createObjectURL(pdfBlob);
        // Re-arm autoPrint for the blob load (the SAS load may already have
        // fired and been skipped as un-printable). setState in an async callback
        // is the intended "external update → setState" pattern.
        autoPrinted.current = false;
        setSasLoad({ forUrl: url, forGeneration: myGeneration, status: 'ready', blobUrl: mintedBlobUrl });
      } catch (err) {
        if (cancelled) return;
        // An abort (cleanup fired mid-fetch) is expected teardown, not a load
        // failure — stay quiet rather than flashing the error state.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        /* CORS / network / non-PDF: never frame the raw url — surface the
           refuse-to-frame error state and let Print fall back to a new tab. */
        setSasLoad({ forUrl: url, forGeneration: myGeneration, status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (mintedBlobUrl) URL.revokeObjectURL(mintedBlobUrl);
    };
  }, [url, isDirectSameOrigin, generation]);

  // Cancel the deferred auto-print timer (see `handleIframeLoad`) whenever the
  // previewed url changes (a new document replaced this one), the preview
  // reopens — even with the identical url, per `generation` (Codex v6 P2's
  // reopened-SAS-url fix applies here too: a same-url reopen is still a new
  // document load and must not let an old pending timer fire against it),
  // the preview closes (`close()` sets `url` to null, re-running this
  // effect), or the component unmounts. Without this a ~60ms `setTimeout`
  // scheduled against the OLD iframe's `contentWindow` could still fire
  // afterward — printing a stale/gone document, or throwing against a
  // torn-down window.
  useEffect(() => {
    return clearAutoPrintTimer;
  }, [url, generation, clearAutoPrintTimer]);

  const openInNewTab = useCallback(() => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  /**
   * Reliable Print. A same-origin document (blob/data, or a SAS we fetched into
   * a blob) prints via `contentWindow.print()`; otherwise we route through a
   * top-level tab, which is NOT subject to the cross-origin iframe SecurityError.
   */
  const handlePrint = useCallback(() => {
    if (printableInline) {
      const win = iframeRef.current?.contentWindow;
      if (win) {
        try {
          win.focus();
          win.print();
          return;
        } catch {
          /* fall through to the top-level tab */
        }
      }
    }
    openInNewTab();
  }, [openInNewTab, printableInline]);

  const handleIframeLoad = useCallback(() => {
    if (!autoPrint || autoPrinted.current) return;
    // Only auto-print a same-origin document; a cross-origin SAS iframe throws.
    if (!printableInline) return;
    autoPrinted.current = true;
    const win = iframeRef.current?.contentWindow;
    // The url + open-generation this deferred print was scheduled for —
    // captured now, checked again when the timer fires (see below). Both are
    // required: a reopen of the IDENTICAL SAS url bumps `generation` without
    // changing `url`, and still counts as a different document (Codex v6 P2).
    const scheduledForUrl = url;
    const scheduledForGeneration = generation;
    // Any previous pending timer belongs to a document this `load` already
    // superseded; it would otherwise still be live (Codex v6 P2).
    clearAutoPrintTimer();
    // Defer a tick so the embedded PDF viewer has painted before we ask it to
    // print (mirrors the sticker-print load cadence). Tracked in a ref so
    // close/unmount/url-change can cancel it before it fires (see the cleanup
    // effect above) — the timer retains this `win`/iframe closure, and firing
    // it after the dialog closed, the url changed, or the component unmounted
    // would print a stale or gone document (Codex v6 P2).
    autoPrintTimer.current = setTimeout(() => {
      autoPrintTimer.current = null;
      // Belt-and-braces re-check: even though close()/url-change/unmount clear
      // this timer via the effect above, re-verify the SAME preview (by url
      // AND generation) is still the one open in the shared store before
      // touching `win` — the store is the single source of truth for "what's
      // open right now", and generation catches a same-url reopen too.
      const current = usePrintPreviewStore.getState();
      if (current.url !== scheduledForUrl || current.generation !== scheduledForGeneration) {
        return;
      }
      try {
        win?.focus();
        win?.print();
      } catch {
        /* no-op — the visible Print button remains the reliable path. */
      }
    }, 60);
  }, [autoPrint, printableInline, url, generation, clearAutoPrintTimer]);

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="tertiary" onClick={close}>
            Close
          </Button>
          <Button
            variant="secondary"
            onClick={openInNewTab}
            icon={<ExternalLink className="size-4" />}
            iconPosition="leading"
          >
            Open in new tab
          </Button>
          <Button
            variant="primary"
            onClick={handlePrint}
            icon={<Printer className="size-4" />}
            iconPosition="leading"
          >
            Print
          </Button>
        </>
      }
    >
      {/* Only mount the iframe while we have a VALIDATED same-origin document
          (a direct blob:/data: url, or a SAS url we fetched + magic-byte
          -verified into a same-origin blob) so closing tears down the
          embedded viewer (and stops any in-flight SAS fetch). A raw SAS url is
          never framed — pending / failed loads render the states below. */}
      {iframeUrl ? (
        <div data-ppv="frame" className="h-print-preview w-full overflow-hidden rounded-md border border-border-default bg-surface-card">
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title={title}
            onLoad={handleIframeLoad}
            className="h-full w-full border-0"
          />
        </div>
      ) : sasLoadFailed && url ? (
        // Refuse to frame the un-validated response (PROD-1001 second review,
        // P1) — a same-origin url could reach the parent window + stored
        // credentials, a cross-origin one could show phishing UI. The only
        // path left is a top-level, no-opener/no-referrer navigation, which
        // does not carry either risk.
        <Banner variant="error" title="Couldn't load the document for preview">
          <p>
            The document couldn&rsquo;t be verified as a PDF, so it hasn&rsquo;t
            been shown here.
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-ppv="link"
            className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand-primary hover:underline"
          >
            <ExternalLink className="size-4" />
            Open in new tab
          </a>
        </Banner>
      ) : url ? (
        <div data-ppv="loading" className="flex h-print-preview w-full items-center justify-center rounded-md border border-border-default bg-surface-card">
          <BrandLoading size={48} label="Loading document preview…" />
        </div>
      ) : null}
    </Modal>
  );
}
